import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { useGuestStore } from "feat/guest/guest.store";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { isFrontmostStack, selectTab } from "feat/nativeNavigation/NativeRootController";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useBoundStore } from "state/bound.store";
import { selectHasTeams } from "state/hooks/useHasTeams";
import { useShallow } from "zustand/react/shallow";
import { isPathTabDisabled, isTabDisabled, useTabDisabledStore } from "./tabDisabled.store";
import { HOME_TAB, isTabPresented, resolveTabForPath, type TabAvailability } from "./tabs";

/** Whether the native bar has no tab for `pathname`. Always false on web. */
function isTabAbsentForPath(pathname: string, availability: TabAvailability): boolean {
  if (!isIosNativeNavigation()) return false;
  const tab = resolveTabForPath(pathname);
  if (!tab) return false;
  return !isTabPresented(tab.id, availability);
}

/**
 * Redirects out of a location whose tab is disabled or absent from the native bar,
 * covering the entry points that do not check: `<Link>`, `useNavigate`, browser back and
 * a route restored on cold start. Redirects silently, without calling `onSelect`.
 */
export const DisabledTabGuard = () => {
  const { pathname } = useLocation();
  const disabledTabs = useTabDisabledStore((s) => s.tabs);
  const isGuestMode = useGuestStore((s) => s.isGuestMode);
  const flags = useBoundStore(
    useShallow((s) => ({
      chatEnabled: s.chatEnabled,
      teamPageEnabled: s.teamPageEnabled,
      hasTeams: selectHasTeams(s),
    })),
  );
  const { replacePath } = useGlobalRouter();
  const { stack } = useNativeNavigationViewContext();

  useEffect(() => {
    const availability = { ...flags, isGuestMode };
    if (!isTabAbsentForPath(pathname, availability) && !isPathTabDisabled(pathname)) return;
    // Home is always presented; disabled, there is nowhere to go.
    if (isTabDisabled(HOME_TAB.id)) return;

    void (async () => {
      // Every tab's view runs this guard, so only the frontmost may move the bar.
      if (isIosNativeNavigation() && !(await isFrontmostStack(stack))) return;
      await replacePath(HOME_TAB.route);
      // A route change does not move the native bar's own selection.
      if (isIosNativeNavigation()) await selectTab(HOME_TAB.id);
    })();
  }, [pathname, disabledTabs, flags, isGuestMode, stack, replacePath]);

  return null;
};
